import * as React from 'react';
import { useDebounceCallback } from '../../../../hooks/useDebounceValue';
import { TicketPriority } from '../../../../models/Support/SupportEnums';
import { stripPlaceholderImages } from '../../../pages/AdminSupportInboxPage/TicketComposer/useComposerDraft';

const NEW_TICKET_DRAFT_VERSION = 2;
const NEW_TICKET_DRAFT_PERSIST_DELAY_MS = 500;

export interface NewTicketDraftAccount {
	id: string;
	name?: string;
}

export interface NewTicketDraftContact {
	userId?: string;
	email?: string;
	name?: string;
}

export interface StoredNewTicketDraft {
	version: typeof NEW_TICKET_DRAFT_VERSION;
	subject: string;
	descriptionHtml: string;
	priority: TicketPriority;
	categoryId: string;
	mailboxId: string;
	account: NewTicketDraftAccount | null;
	contact: NewTicketDraftContact | null;
	manualContact: boolean;
	updatedAt: string;
}

/**
 * Per-agent, per-launch-context draft storage so closing the modal never drops an in-progress
 * ticket. The account details page keys by account; the support inbox shares one draft.
 */
export const getNewTicketDraftStorageKey = (userId: string, accountId?: string): string =>
	`levitate:support-new-ticket-draft:${userId}:${accountId ? `account:${accountId}` : 'inbox'}`;

const isTicketPriority = (value: unknown): value is TicketPriority =>
	typeof value === 'string' && (Object.values(TicketPriority) as string[]).includes(value);

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const readAccount = (value: unknown): NewTicketDraftAccount | null => {
	if (typeof value !== 'object' || value === null) return null;
	const account = value as Partial<NewTicketDraftAccount>;
	if (typeof account.id !== 'string' || !account.id) return null;
	return { id: account.id, name: typeof account.name === 'string' ? account.name : undefined };
};

const readContact = (value: unknown): NewTicketDraftContact | null => {
	if (typeof value !== 'object' || value === null) return null;
	const contact = value as Partial<NewTicketDraftContact>;
	const userId = typeof contact.userId === 'string' && contact.userId ? contact.userId : undefined;
	const email = typeof contact.email === 'string' && contact.email ? contact.email : undefined;
	const name = typeof contact.name === 'string' && contact.name ? contact.name : undefined;
	if (!userId && !email && !name) return null;
	return { userId, email, name };
};

export const hasNewTicketDraftData = (
	draft: Pick<StoredNewTicketDraft, 'subject' | 'descriptionHtml' | 'categoryId' | 'contact'>
): boolean =>
	draft.subject.trim().length > 0 ||
	draft.descriptionHtml.trim().length > 0 ||
	draft.categoryId.length > 0 ||
	draft.contact !== null;

export const readStoredNewTicketDraft = (storageKey: string): StoredNewTicketDraft | null => {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<StoredNewTicketDraft>;
		if (parsed.version !== NEW_TICKET_DRAFT_VERSION) return null;
		const draft: StoredNewTicketDraft = {
			version: NEW_TICKET_DRAFT_VERSION,
			subject: readString(parsed.subject),
			descriptionHtml: readString(parsed.descriptionHtml),
			priority: isTicketPriority(parsed.priority) ? parsed.priority : TicketPriority.Normal,
			categoryId: readString(parsed.categoryId),
			mailboxId: readString(parsed.mailboxId),
			account: readAccount(parsed.account),
			contact: readContact(parsed.contact),
			manualContact: parsed.manualContact === true,
			updatedAt: readString(parsed.updatedAt),
		};
		if (!hasNewTicketDraftData(draft)) return null;
		return draft;
	} catch {
		return null;
	}
};

const writeStoredNewTicketDraft = (storageKey: string, draft: StoredNewTicketDraft) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(storageKey, JSON.stringify(draft));
	} catch {
		// Storage can be unavailable or full; keep the in-memory form usable either way.
	}
};

const removeStoredNewTicketDraft = (storageKey: string) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.removeItem(storageKey);
	} catch {
		// Ignore storage failures; successful submits still reset the in-memory form.
	}
};

export interface NewTicketDraftOptions {
	storageKey: string;
	subject: string;
	descriptionHtml: string;
	priority: TicketPriority;
	categoryId: string;
	mailboxId: string;
	account: NewTicketDraftAccount | null;
	contact: NewTicketDraftContact | null;
	manualContact: boolean;
}

const persistNewTicketDraft = ({ storageKey, ...fields }: NewTicketDraftOptions) => {
	const draft: StoredNewTicketDraft = {
		version: NEW_TICKET_DRAFT_VERSION,
		...fields,
		// Blob placeholder URLs from in-flight image uploads die with the session; keep only
		// images that already have their hosted URL (same rule as the composer's drafts).
		descriptionHtml: stripPlaceholderImages(fields.descriptionHtml),
		updatedAt: new Date().toISOString(),
	};
	if (!hasNewTicketDraftData(draft)) {
		removeStoredNewTicketDraft(storageKey);
		return;
	}
	writeStoredNewTicketDraft(storageKey, draft);
};

/**
 * Persists the draft on every change (debounced) and flushes the pending write on unmount, so
 * closing the modal right after an edit still keeps it. `clearDraft` (a successful submission)
 * removes the stored draft and suppresses the unmount flush that would otherwise resurrect it.
 */
export const useNewTicketDraft = (options: NewTicketDraftOptions) => {
	const { storageKey, subject, descriptionHtml, priority, categoryId, mailboxId, account, contact, manualContact } =
		options;
	const draftState = React.useMemo(
		() => ({ storageKey, subject, descriptionHtml, priority, categoryId, mailboxId, account, contact, manualContact }),
		[storageKey, subject, descriptionHtml, priority, categoryId, mailboxId, account, contact, manualContact]
	);
	const draftStateRef = React.useRef(draftState);
	draftStateRef.current = draftState;
	const clearedRef = React.useRef(false);

	const persistDraft = useDebounceCallback((nextDraftState: NewTicketDraftOptions) => {
		if (clearedRef.current) return;
		persistNewTicketDraft(nextDraftState);
	}, NEW_TICKET_DRAFT_PERSIST_DELAY_MS);

	React.useEffect(() => {
		persistDraft(draftState);
		return () => persistDraft.cancel();
	}, [draftState, persistDraft]);

	React.useEffect(() => {
		return () => {
			if (clearedRef.current) return;
			persistNewTicketDraft(draftStateRef.current);
		};
	}, []);

	const clearDraft = React.useCallback(() => {
		clearedRef.current = true;
		removeStoredNewTicketDraft(storageKey);
	}, [storageKey]);

	return { clearDraft };
};
